import React from 'react';
import Tooltip from '@mui/material/Tooltip';

type ColumnKeys =
  | 'name'
  | 'status_flag'
  | 'match_probability'
  | 'risk_level'
  | 'hit_count'
  | 'change_since_last_screen';

interface IColumnsProps {
  isSelectedItems?: boolean;
  handleCheckboxClick?: () => void;
  showFilters: boolean;
  sortData: (key: string) => void;
  sortConfig: { key: string; direction: string };
}

const Columns: React.FC<IColumnsProps> = ({
  isSelectedItems,
  handleCheckboxClick,
  sortData,
  sortConfig,
}) => {
  const getSortIcon = (key: ColumnKeys) => {
    if (sortConfig.key !== key) {
      // Default / unsorted icon (optional, can be a dual arrow)
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
        >
          <path
            d='M8 4V12M8 4L4 8M8 4L12 8'
            stroke='var(--card-text)'
            strokeOpacity='0.5'
            strokeWidth='1.33'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      );
    }
    if (sortConfig.direction === 'asc') {
      // Ascending icon
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
        >
          <path
            d='M8 12.6666V3.33331M8 3.33331L3.33333 7.99998M8 3.33331L12.6667 7.99998'
            stroke='var(--card-text)'
            strokeWidth='1.33'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      );
    }
    // Descending icon
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
      >
        <path
          d='M8 3.33331V12.6666M8 12.6666L3.33333 7.99998M8 12.6666L12.6667 7.99998'
          stroke='var(--card-text)'
          strokeWidth='1.33'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    );
  };

  const CheckboxIcon = () =>
    isSelectedItems ? (
      <div className='full-list-filters-cont-row-icon-item'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='12'
          height='12'
          viewBox='0 0 12 12'
          fill='none'
        >
          <path
            d='M10 3L4.5 8.5L2 6'
            stroke='var(--text-primary)'
            strokeWidth='1.6666'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
    ) : (
      <div className='full-list-filters-cont-row-icon-item-unselected'></div>
    );

  return (
    <div className='screening-results-columns'>
      <div
        className='screening-table-placeholder-column'
        style={{
          borderTopLeftRadius: 'var(--radius-xl, 12px)',
          width: '64px',
          minWidth: '64px',
          padding: '12px',
          cursor: 'pointer',
        }}
      >
        <div
          className='screening-results-row-default-cell-inner'
          onClick={handleCheckboxClick}
        >
          <CheckboxIcon />
        </div>
      </div>
      <div className='screening-table-title-column'>
        <div
          className='screening-table-title-column-inner'
          onClick={() => sortData('name')}
        >
          <div className='screening-table-column-text'>
            Name {getSortIcon('name')}
          </div>
        </div>
      </div>
      <div className='screening-table-title-column'>
        <div
          className='screening-table-title-column-inner'
          onClick={() => sortData('status_flag')}
        >
          <div className='screening-table-column-text'>
            Status {getSortIcon('status_flag')}
          </div>
        </div>
      </div>
      <div className='screening-table-title-column'>
        <div
          className='screening-table-title-column-inner'
          onClick={() => sortData('risk_level')}
        >
          <div className='screening-table-column-text'>
            Assigned Risk {getSortIcon('risk_level')}
          </div>
        </div>
      </div>
      <div className='screening-table-title-column'>
        <div className='screening-table-title-column-inner'>
          <Tooltip
            title='Watchlists + Beneficial Ownership'
            arrow
            placement='top'
          >
            <div className='screening-table-column-text'>Lenses</div>
          </Tooltip>
        </div>
      </div>
      <div
        className='screening-table-title-column'
        style={{ justifyContent: 'center' }}
      >
        <div
          className='screening-table-title-column-inner'
          onClick={() => sortData('hit_count')}
        >
          <Tooltip
            title='Watchlists + Beneficial Ownership'
            arrow
            placement='top'
          >
            <div className='screening-table-column-text'>
              Matched Hits {getSortIcon('hit_count')}
            </div>
          </Tooltip>
        </div>
      </div>
      <div
        className='screening-table-title-column'
        style={{
          borderTopRightRadius: 'var(--radius-xl, 12px)',
          justifyContent: 'center',
        }}
      >
        <div
          className='screening-table-title-column-inner'
          onClick={() => sortData('change_since_last_screen')}
        >
          <div className='screening-table-column-text'>
            Change since last screen {getSortIcon('change_since_last_screen')}
          </div>
        </div>
      </div>
      <div
        className='screening-table-placeholder-column'
        style={{
          borderTopRightRadius: 'var(--radius-xl, 12px)',
          width: '56px',
        }}
      >
        <div className='screening-results-row-default-cell-inner'></div>
      </div>
    </div>
  );
};

export default Columns;
